import { v4 } from '@lukeed/uuid';
import { levenbergMarquardt } from 'ml-levenberg-marquardt';
import { getShape1D } from 'ml-peak-shape-generator';
import { xMinMaxValues } from 'ml-spectra-processing';
import { directOptimization } from './optimization/directOptimization';
import { getInternalSignals, } from './optimization/getInternalSignals';
import { getSumOfShapes } from './optimization/getSumOfShapes';
import { signalsToPointXY, } from './optimization/signalsToPointXY';
const defaultLMOptimizationOptions = {
    damping: 1.5,
    maxIterations: 100,
    errorTolerance: 1e-8,
};
const defaultDirectOptimizationOptions = {
    iterations: 25,
};
export function optimizeSignals(data, signals, options) {
    const { optimization = {}, simulation: simulationOptions, ...restOptions } = options;
    let temp = xMinMaxValues(data.y);
    const minMaxY = { ...temp, range: temp.max - temp.min };
    const internalSignals = getInternalSignals(data, signals, minMaxY, {
        frequency: simulationOptions.frequency,
        ...restOptions,
    });
    const { baseline: shiftValue = minMaxY.min } = options;
    let normalizedY = new Float64Array(data.y.length);
    for (let i = 0; i < data.y.length; i++) {
        normalizedY[i] = (data.y[i] - shiftValue) / minMaxY.range;
    }
    const nbParams = internalSignals[internalSignals.length - 1].toIndexCoupling + 1;
    const minValues = new Float64Array(nbParams);
    const maxValues = new Float64Array(nbParams);
    const initialValues = new Float64Array(nbParams);
    const gradientDifferences = new Float64Array(nbParams);
    let index = 0;
    for (const internalSignal of internalSignals) {
        for (let i = 0; i < internalSignal.parameters.length; i++) {
            minValues[index] = internalSignal.propertiesValues.min[i];
            maxValues[index] = internalSignal.propertiesValues.max[i];
            initialValues[index] = internalSignal.propertiesValues.init[i];
            gradientDifferences[index++] =
                internalSignal.propertiesValues.gradientDifference[i];
        }
        for (let i = internalSignal.parameters.length; i < internalSignal.parameters.length + internalSignal.signal.js.length; i++) {
            minValues[index] = internalSignal.propertiesValues.min[i];
            maxValues[index] = internalSignal.propertiesValues.max[i];
            initialValues[index] = internalSignal.propertiesValues.init[i];
            gradientDifferences[index++] =
                internalSignal.propertiesValues.gradientDifference[i];
        }
    }
    const sumOfShapes = getSumOfShapes(internalSignals, simulationOptions);
    const { kind, options: optimizationOptions } = optimization;
    const [algorithm, defaultOptimizationOptions] = kind === 'direct'
        ? [directOptimization, defaultDirectOptimizationOptions]
        : [levenbergMarquardt, defaultLMOptimizationOptions];
    let fitted = algorithm({ x: data.x, y: normalizedY }, sumOfShapes, {
        minValues,
        maxValues,
        initialValues,
        gradientDifference: gradientDifferences,
        ...defaultOptimizationOptions,
        ...optimizationOptions,
    });
    const fittedValues = fitted.parameterValues;
    const newSignals = [];
    for (const internalSignal of internalSignals) {
        const { fromIndexCoupling } = internalSignal;
        const js = internalSignal.signal.js.map((jCoupling, i) => {
            jCoupling.coupling = fittedValues[fromIndexCoupling + i];
            return jCoupling;
        });
        const delta = fittedValues[internalSignal.fromIndex];
        const pointXYs = signalsToPointXY([{ delta, js }], simulationOptions);
        const intensity = fittedValues[internalSignal.fromIndex + 1] * minMaxY.range + shiftValue;
        const newPeaks = [];
        for (const { x, y } of pointXYs) {
            const peak = {
                id: v4(),
                x,
                y: intensity * y,
                width: 0,
                shape: { ...internalSignal.shape },
            };
            for (let i = 2; i < internalSignal.parameters.length; i++) {
                //@ts-expect-error should be fixed once
                peak.shape[internalSignal.parameters[i]] =
                    fittedValues[internalSignal.fromIndex + i];
            }
            peak.shape.fwhm *= simulationOptions.frequency;
            peak.width = getShape1D(peak.shape).fwhmToWidth(peak.shape.fwhm);
            newPeaks.push(peak);
        }
        newSignals.push({
            delta,
            js,
            shape: { ...newPeaks[0].shape },
            intensity,
            peaks: newPeaks,
        });
    }
    return newSignals;
}
//# sourceMappingURL=optimizeSignals.js.map